import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

// Silence generic cross-origin "Script error." events (adblockers, GA4/PostHog blocked, etc.)
// so the CRA dev-overlay doesn't hijack the UI in preview. Production build is unaffected.
// Uses capture:true to intercept BEFORE the CRA dev overlay handler.
if (typeof window !== "undefined") {
  window.addEventListener("error", (e) => {
    const msg = e.message || "";
    const src = e.filename || "";
    const isScriptError = msg === "Script error." || msg === "Script error" || !msg;
    const isThirdParty = src && !src.includes(window.location.host);
    if (isScriptError || isThirdParty) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  }, true);
  window.addEventListener("unhandledrejection", (e) => {
    const msg = (e.reason && (e.reason.message || String(e.reason))) || "";
    if (!msg || msg === "Script error." || msg === "Script error") {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);
}
import { Toaster } from "sonner";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartSheet from "@/components/CartSheet";
import NewsletterPopup from "@/components/NewsletterPopup";
import WhatsAppFab from "@/components/WhatsAppFab";
import CookieBanner, { getConsent, CONSENT_EVENT } from "@/components/CookieBanner";
import HomePage from "@/pages/HomePage";
import CatalogPage from "@/pages/CatalogPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import StoryPage from "@/pages/StoryPage";
import ContactPage from "@/pages/ContactPage";
import { BlogListPage, BlogPostPage } from "@/pages/BlogPages";
import WishlistPage from "@/pages/WishlistPage";
import LegalPage from "@/pages/LegalPage";
import LoginPage from "@/pages/LoginPage";
import AdminPage from "@/pages/AdminPage";
import CustomerAuthPage from "@/pages/CustomerAuthPage";
import AccountPage from "@/pages/AccountPage";
import PacksPage from "@/pages/PacksPage";
import PasswordResetPage from "@/pages/PasswordResetPage";

function useGA() {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const check = () => setEnabled(!!getConsent()?.analytics);
    check();
    window.addEventListener(CONSENT_EVENT, check);
    return () => window.removeEventListener(CONSENT_EVENT, check);
  }, []);
  useEffect(() => {
    const id = process.env.REACT_APP_GA_ID;
    if (!id) return;
    const existing = document.getElementById("ga-tag");
    if (enabled) {
      if (existing) return; // already loaded
      const s = document.createElement("script");
      s.id = "ga-tag";
      s.async = true;
      s.crossOrigin = "anonymous";
      s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
      s.onerror = () => { /* GA4 blocked by adblocker or network — silent */ };
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { try { window.dataLayer.push(arguments); } catch (e) {} };
      try {
        window.gtag("js", new Date());
        window.gtag("config", id, { anonymize_ip: true, send_page_view: false });
        window.gtag("event", "page_view", {
          page_path: window.location.pathname + window.location.search,
          page_title: document.title,
        });
      } catch (e) { /* GA4 init failed — silent */ }
    } else if (existing) {
      // User revoked analytics after having accepted — deactivate immediately.
      try { window.gtag && window.gtag("consent", "update", { analytics_storage: "denied" }); } catch {}
      existing.remove();
      try { delete window.gtag; } catch { window.gtag = undefined; }
      window.dataLayer = [];
    }
  }, [enabled]);
}

function GAPageTracker() {
  const location = useLocation();
  useEffect(() => {
    const id = process.env.REACT_APP_GA_ID;
    if (!id || !window.gtag || !getConsent()?.analytics) return;
    try {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    } catch (e) { /* GA4 pageview failed — silent */ }
  }, [location]);
  return null;
}

function App() {
  useGA();
  return (
    <div className="App min-h-screen flex flex-col bg-[color:var(--brand-bg)]">
      <BrowserRouter>
        <AuthProvider>
          <WishlistProvider>
            <CartProvider>
              <GAPageTracker />
              <Header />
              <CartSheet />
              <NewsletterPopup />
              <main className="flex-1">
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/tienda" element={<CatalogPage />} />
                  <Route path="/producto/:slug" element={<ProductDetailPage />} />
                  <Route path="/historia" element={<StoryPage />} />
                  <Route path="/contacto" element={<ContactPage />} />
                  <Route path="/blog" element={<BlogListPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />
                  <Route path="/favoritos" element={<WishlistPage />} />
                  <Route path="/payment/success" element={<PaymentSuccessPage />} />
                  <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                  <Route path="/aviso-legal" element={<LegalPage />} />
                  <Route path="/privacidad" element={<LegalPage />} />
                  <Route path="/cookies" element={<LegalPage />} />
                  <Route path="/condiciones" element={<LegalPage />} />
                  <Route path="/envios" element={<LegalPage />} />
                  <Route path="/admin/login" element={<LoginPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/cuenta/entrar" element={<CustomerAuthPage />} />
                  <Route path="/cuenta" element={<AccountPage />} />
                  <Route path="/packs" element={<PacksPage />} />
                  <Route path="/cuenta/recuperar" element={<PasswordResetPage />} />
                </Routes>
              </main>
              <Footer />
              <WhatsAppFab />
              <CookieBanner />
              <Toaster position="bottom-right" richColors closeButton />
            </CartProvider>
          </WishlistProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
